import React, { useState, useMemo } from 'react';
import TopBar from '../layout/TopBar';
import StatusBadge from '../components/StatusBadge';
import { APPROVAL_QUEUE, DEPARTMENTS } from '../data/mockData';
import { useHGS } from '../context/HGSContext';
import { Check, X, MessageSquare, Filter, Clock, Send, Database, Code2, AlertTriangle, FileSpreadsheet, Layers, ClipboardList, Building2, Lock } from 'lucide-react';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';

export default function ApprovalsPage() {
  const { pendingApprovals, approveMasterSubmission, rejectMasterSubmission, addApprovalComment, formResponses, advanceApproval, user } = useHGS();
  const [tab, setTab] = useState('responses');

  const masterCounts = useMemo(() => ({
    pending: pendingApprovals.filter((s) => s.status === 'Pending').length,
    total: pendingApprovals.length,
  }), [pendingApprovals]);

  const responseSubs = useMemo(() =>
    Object.entries(formResponses || {})
      .map(([k, v]) => ({ key: k, ...v }))
      .filter((r) => r.workflowStatus && r.workflowStatus !== 'FinalApproved' && r.workflowStatus !== 'Rejected' && r.workflowStatus !== 'Draft'),
    [formResponses]);

  if (user?.role === 'department_head') {
    return (
      <div>
        <TopBar title="Approval Review" subtitle="Review & approve department submissions" breadcrumbs={['HGS', 'Approvals']} />
        <div className="p-8">
          <div className="bg-white rounded-xl border border-amber-200 p-6 flex items-start gap-4" data-testid="approvals-forbidden">
            <div className="w-12 h-12 rounded-xl bg-amber-100 text-amber-700 flex items-center justify-center flex-shrink-0">
              <Lock className="w-5 h-5" />
            </div>
            <div>
              <h3 className="text-base font-bold text-slate-900">Approvals is restricted</h3>
              <p className="text-sm text-slate-600 mt-1">
                Department Heads fill assigned tasks; approval is owned by Division Head and Operating Head. Head back to <span className="font-bold">My Dashboard</span> to see your tasks.
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div>
      <TopBar
        title="Approval Review"
        subtitle="3-stage workflow: Department Head submits → Division Head reviews → Operating Head approves"
        breadcrumbs={['HGS', 'Approvals']}
      />
      <div className="p-8 space-y-5">
        <div className="flex items-center gap-1 border-b border-slate-200 flex-wrap">
          <TabBtn
            active={tab === 'responses'}
            onClick={() => setTab('responses')}
            icon={ClipboardList}
            label="Response Approvals"
            count={responseSubs.length}
            highlight={responseSubs.length}
            testId="tab-response-approvals"
          />
          <TabBtn
            active={tab === 'general'}
            onClick={() => setTab('general')}
            icon={Layers}
            label="General Submissions"
            count={APPROVAL_QUEUE.length}
            testId="tab-general-approvals"
          />
          <TabBtn
            active={tab === 'master'}
            onClick={() => setTab('master')}
            icon={Database}
            label="Master Approvals"
            count={masterCounts.total}
            highlight={masterCounts.pending}
            testId="tab-master-approvals"
          />
        </div>

        {tab === 'responses' && <ResponseApprovals subs={responseSubs} user={user} advance={advanceApproval} />}
        {tab === 'general' && <GeneralApprovals />}
        {tab === 'master' && (
          <MasterApprovalsPanel
            submissions={pendingApprovals}
            onApprove={approveMasterSubmission}
            onReject={rejectMasterSubmission}
            onComment={addApprovalComment}
          />
        )}
      </div>
    </div>
  );
}

function ResponseApprovals({ subs, user, advance }) {
  const role = user?.role;
  const canDivisionApprove = role === 'division_head' || role === 'admin' || role === 'operating_head';
  const canFinalApprove = role === 'operating_head' || role === 'admin';

  const [deptFilter, setDeptFilter] = React.useState('all');
  const [moduleFilter, setModuleFilter] = React.useState('all');

  const depts = React.useMemo(() => {
    const set = new Set();
    subs.forEach((s) => { if (s.submittedDepartment) set.add(s.submittedDepartment); });
    return ['all', ...Array.from(set)];
  }, [subs]);

  const filtered = React.useMemo(() => subs.filter((s) =>
    (deptFilter === 'all' || s.submittedDepartment === deptFilter) &&
    (moduleFilter === 'all' || s.moduleKey === moduleFilter)
  ), [subs, deptFilter, moduleFilter]);

  // Dept rollup for Division Head summary
  const rollup = React.useMemo(() => {
    const map = {};
    subs.forEach((s) => {
      const d = s.submittedDepartment || 'unassigned';
      if (!map[d]) map[d] = { total: 0, submitted: 0, underReview: 0, approved: 0 };
      map[d].total++;
      if (s.workflowStatus === 'Submitted') map[d].submitted++;
      if (s.workflowStatus === 'UnderReview') map[d].underReview++;
      if (s.workflowStatus === 'Approved') map[d].approved++;
    });
    return map;
  }, [subs]);

  if (subs.length === 0) {
    return (
      <div className="bg-white rounded-xl border border-slate-200 p-12 text-center" data-testid="response-approvals-empty">
        <ClipboardList className="w-10 h-10 mx-auto text-slate-300 mb-3" />
        <p className="text-sm font-bold text-slate-700">No responses awaiting approval</p>
        <p className="text-xs text-slate-500 mt-1 max-w-md mx-auto">
          When a Department Head submits a Fraud / PCC / DLC response, it appears here for Division Head review, then Operating Head final approval.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="response-approvals-list">
      {/* Dept rollup — visible to Division Head, Operating Head, Admin */}
      {(role === 'division_head' || role === 'operating_head' || role === 'admin') && Object.keys(rollup).length > 1 && (
        <div className="bg-white rounded-xl border border-slate-200 p-4" data-testid="approvals-dept-rollup">
          <div className="flex items-center gap-2 mb-3">
            <Building2 className="w-3.5 h-3.5 text-slate-500" />
            <span className="text-[11px] font-bold text-slate-700 uppercase tracking-wider">Department Roll-up</span>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-2">
            {DEPARTMENTS.filter((d) => rollup[d.id]).map((d) => {
              const r = rollup[d.id];
              return (
                <button
                  key={d.id}
                  data-testid={`rollup-${d.id}`}
                  onClick={() => setDeptFilter(d.id)}
                  className={cn(
                    'text-left p-2.5 rounded-lg border transition',
                    deptFilter === d.id ? 'border-[#CC0000] bg-[#CC0000]/5' : 'border-slate-200 bg-slate-50 hover:bg-white'
                  )}
                >
                  <div className="text-[10px] font-bold text-slate-700 uppercase">{d.label}</div>
                  <div className="text-lg font-bold text-slate-900 tabular-nums">{r.total}</div>
                  <div className="text-[9px] text-slate-500">
                    {r.submitted} new · {r.underReview} review · {r.approved} forwarded
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Filter bar */}
      <div className="flex items-center gap-2 flex-wrap">
        <Filter className="w-3.5 h-3.5 text-slate-400" />
        <select
          data-testid="approvals-filter-dept"
          value={deptFilter}
          onChange={(e) => setDeptFilter(e.target.value)}
          className="text-xs border border-slate-200 rounded-md px-2 py-1.5 bg-white"
        >
          {depts.map((d) => <option key={d} value={d}>{d === 'all' ? 'All departments' : (DEPARTMENTS.find((x) => x.id === d)?.label || d)}</option>)}
        </select>
        <select
          data-testid="approvals-filter-module"
          value={moduleFilter}
          onChange={(e) => setModuleFilter(e.target.value)}
          className="text-xs border border-slate-200 rounded-md px-2 py-1.5 bg-white"
        >
          {['all', 'fraud', 'pcc', 'dlc'].map((m) => <option key={m} value={m}>{m === 'all' ? 'All modules' : m.toUpperCase()}</option>)}
        </select>
        <span className="text-[11px] text-slate-500">Showing {filtered.length} of {subs.length}</span>
      </div>

      {filtered.length === 0 && (
        <div className="bg-white rounded-xl border border-slate-200 p-8 text-center" data-testid="approvals-empty-filtered">
          <p className="text-sm font-semibold text-slate-700">No submissions match these filters</p>
        </div>
      )}

      {filtered.map((s) => {
        const dept = DEPARTMENTS.find((d) => d.id === s.submittedDepartment);
        return (
          <div key={s.key} className="bg-white rounded-xl border border-slate-200 p-5" data-testid={`approval-${s.key}`}>
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 rounded-lg bg-slate-100 text-slate-700 flex items-center justify-center font-bold text-[11px] uppercase flex-shrink-0">
                {s.moduleKey}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap mb-1">
                  <span className="font-mono text-[11px] font-bold text-slate-800">{s.recordId}</span>
                  {dept && (
                    <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-slate-100 text-slate-700 text-[10px] font-bold">
                      <Building2 className="w-2.5 h-2.5" />
                      {dept.label}
                    </span>
                  )}
                  <StatusBadge status={s.workflowStatus === 'Submitted' ? 'Pending Review' : s.workflowStatus === 'UnderReview' ? 'In Progress' : s.workflowStatus === 'Approved' ? 'Pending Final' : 'Pending'} />
                </div>
                <p className="text-sm font-semibold text-slate-900 mb-1">Response submitted by {s.submittedBy}</p>
                <p className="text-[11px] text-slate-500">
                  {new Date(s.submittedAt).toLocaleString()} · Response value: <span className="font-mono font-bold text-slate-700">{s.values?.responseType || '—'}</span>
                </p>

                {/* History */}
                {s.history && s.history.length > 0 && (
                  <div className="mt-3 pt-3 border-t border-slate-100">
                    <div className="text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-1.5">Audit trail</div>
                    <div className="space-y-1">
                      {s.history.map((h, i) => (
                        <div key={`${h.at || i}-${h.action || ''}`} className="text-[11px] text-slate-600 flex items-center gap-2">
                          <span className="w-1 h-1 rounded-full bg-slate-400" />
                          <span className="font-semibold">{h.action}</span>
                          <span>by {h.by}</span>
                          <span className="text-slate-400">· {new Date(h.at).toLocaleString()}</span>
                          {h.comment && <span className="text-slate-500 italic">— "{h.comment}"</span>}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
              <div className="flex-shrink-0 flex flex-col gap-2">
                {s.workflowStatus === 'Submitted' && canDivisionApprove && (
                  <button
                    data-testid={`action-div-approve-${s.key}`}
                    onClick={() => { advance(s.key, 'approve-division', 'Division review passed'); toast.success('Division approved · forwarded for final approval'); }}
                    className="px-3 py-1.5 rounded-lg bg-sky-600 hover:bg-sky-700 text-white text-[11px] font-bold"
                  >
                    <Check className="w-3 h-3 inline mr-1" />
                    Division Approve
                  </button>
                )}
                {s.workflowStatus === 'Approved' && canFinalApprove && (
                  <button
                    data-testid={`action-final-approve-${s.key}`}
                    onClick={() => { advance(s.key, 'approve-final', 'Final approval granted'); toast.success('Final approved'); }}
                    className="px-3 py-1.5 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-[11px] font-bold"
                  >
                    <Check className="w-3 h-3 inline mr-1" />
                    Final Approve
                  </button>
                )}
                {(canDivisionApprove || canFinalApprove) && (
                  <button
                    data-testid={`action-reject-${s.key}`}
                    onClick={() => { advance(s.key, 'reject', 'Requires rework'); toast.error('Rejected'); }}
                    className="px-3 py-1.5 rounded-lg bg-red-50 hover:bg-red-100 text-red-700 text-[11px] font-bold border border-red-200"
                  >
                    <X className="w-3 h-3 inline mr-1" />
                    Reject
                  </button>
                )}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

function TabBtn({ active, onClick, icon: Icon, label, count, highlight, testId }) {
  return (
    <button
      onClick={onClick}
      data-testid={testId}
      className={cn(
        'inline-flex items-center gap-2 px-4 py-2.5 text-xs font-semibold rounded-t-lg border-b-2 transition relative',
        active ? 'text-[#CC0000] border-[#CC0000] bg-red-50/40' : 'border-transparent text-slate-600 hover:text-slate-900'
      )}
    >
      <Icon className="w-3.5 h-3.5" />
      {label}
      {count !== undefined && (
        <span className={cn('ml-1 px-1.5 py-0.5 rounded-md text-[10px] font-bold', active ? 'bg-[#CC0000]/10 text-[#CC0000]' : 'bg-slate-100 text-slate-700')}>
          {count}
        </span>
      )}
      {highlight > 0 && (
        <span className="absolute -top-0.5 -right-0.5 w-2 h-2 rounded-full bg-violet-500 animate-pulse" />
      )}
    </button>
  );
}

// ----- Existing General Submissions block (lifted from old code) -----
function GeneralApprovals() {
  const [items, setItems] = useState(APPROVAL_QUEUE);
  const [activeId, setActiveId] = useState(APPROVAL_QUEUE[0].id);
  const [filter, setFilter] = useState('All');
  const [comment, setComment] = useState('');

  const active = items.find((i) => i.id === activeId);
  const filtered = filter === 'All' ? items : items.filter((i) => i.status === filter);

  const decide = (id, decision) => {
    setItems((arr) => arr.map((it) => (it.id === id ? { ...it, status: decision } : it)));
  };

  const addComment = () => {
    if (!comment.trim() || !active) return;
    setItems((arr) =>
      arr.map((it) =>
        it.id === active.id
          ? { ...it, comments: [...it.comments, { author: 'You (Reviewer)', text: comment.trim(), date: new Date().toISOString().slice(0, 10) }] }
          : it
      )
    );
    setComment('');
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-[420px,1fr] gap-6">
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden flex flex-col">
        <div className="p-4 border-b border-slate-100">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-bold text-slate-900">Submission Queue</h3>
            <span className="text-[10px] text-slate-500">{filtered.length} items</span>
          </div>
          <div className="flex gap-1.5">
            {['All', 'Pending', 'Approved', 'Rejected'].map((f) => (
              <button
                key={f}
                onClick={() => setFilter(f)}
                className={cn(
                  'px-3 py-1.5 rounded-md text-[11px] font-semibold border transition',
                  filter === f ? 'bg-slate-900 text-white border-slate-900' : 'bg-white text-slate-600 border-slate-200 hover:border-slate-400'
                )}
              >
                {f}
              </button>
            ))}
          </div>
        </div>
        <div className="divide-y divide-slate-100 overflow-y-auto max-h-[640px]">
          {filtered.map((it) => (
            <button
              key={it.id}
              onClick={() => setActiveId(it.id)}
              className={cn(
                'w-full text-left p-4 hover:bg-slate-50/60 transition',
                activeId === it.id && 'bg-red-50/40 border-l-2 border-l-[#CC0000]'
              )}
            >
              <div className="flex items-start justify-between gap-2 mb-1">
                <span className="font-mono text-[10px] text-slate-500">{it.id}</span>
                <StatusBadge status={it.status} />
              </div>
              <div className="text-sm font-semibold text-slate-900 leading-snug mb-1">{it.title}</div>
              <div className="flex items-center justify-between text-[11px]">
                <span className="text-slate-500">{it.type} · {it.submittedBy}</span>
                <StatusBadge status={it.priority} dot={false} />
              </div>
            </button>
          ))}
          {filtered.length === 0 && (
            <div className="p-12 text-center">
              <Filter className="w-8 h-8 mx-auto text-slate-300 mb-2" />
              <p className="text-sm font-medium text-slate-500">No submissions in this view</p>
            </div>
          )}
        </div>
      </div>

      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        {active ? (
          <div className="flex flex-col h-full">
            <div className="p-6 border-b border-slate-100">
              <div className="flex items-center gap-2 mb-2">
                <span className="font-mono text-xs text-slate-500">{active.id}</span>
                <StatusBadge status={active.status} />
                <StatusBadge status={active.priority} dot={false} />
              </div>
              <h2 className="text-xl font-bold text-slate-900 leading-tight">{active.title}</h2>
              <div className="flex items-center gap-4 mt-2 text-xs text-slate-500">
                <span><span className="font-medium text-slate-700">{active.type}</span></span>
                <span>Submitted by <span className="font-medium text-slate-700">{active.submittedBy}</span></span>
                <span className="inline-flex items-center gap-1"><Clock className="w-3 h-3" /> {active.submittedOn}</span>
              </div>
            </div>
            <div className="p-6 space-y-5 flex-1">
              <div className="p-4 rounded-lg bg-slate-50 border border-slate-200">
                <div className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider mb-1.5">Summary</div>
                <p className="text-sm text-slate-700 leading-relaxed">
                  The submission contains the full risk record, mitigation plan, supporting evidence and linked compliance items. All required fields have been completed by the submitter.
                </p>
              </div>
              <div>
                <div className="flex items-center gap-2 mb-3">
                  <MessageSquare className="w-4 h-4 text-slate-500" />
                  <h4 className="text-sm font-bold text-slate-900">Comments</h4>
                  <span className="text-[10px] text-slate-500">({active.comments.length})</span>
                </div>
                <div className="space-y-2">
                  {active.comments.length === 0 && <div className="text-xs text-slate-400 italic">No comments yet.</div>}
                  {active.comments.map((c, i) => (
                    <div key={`${c.author || 'c'}-${c.date || i}`} className="p-3 rounded-lg bg-slate-50 border border-slate-200">
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-xs font-bold text-slate-900">{c.author}</span>
                        <span className="text-[10px] text-slate-500">{c.date}</span>
                      </div>
                      <p className="text-xs text-slate-700 leading-relaxed">{c.text}</p>
                    </div>
                  ))}
                </div>
                <div className="mt-3 flex gap-2">
                  <input
                    value={comment}
                    onChange={(e) => setComment(e.target.value)}
                    placeholder="Add a review comment…"
                    className="flex-1 px-3 py-2 bg-white border border-slate-200 rounded-lg text-xs focus:outline-none focus:border-[#CC0000] focus:ring-2 focus:ring-[#CC0000]/10"
                  />
                  <button onClick={addComment} className="px-3 py-2 bg-slate-900 hover:bg-slate-800 text-white text-xs font-semibold rounded-lg inline-flex items-center gap-1.5">
                    <Send className="w-3 h-3" />
                    Post
                  </button>
                </div>
              </div>
            </div>
            <div className="px-6 py-4 bg-slate-50 border-t border-slate-100 flex items-center justify-end gap-3">
              <button
                onClick={() => decide(active.id, 'Rejected')}
                disabled={active.status === 'Rejected'}
                className="inline-flex items-center gap-2 px-4 py-2 bg-white hover:bg-red-50 border border-red-200 text-[#CC0000] text-sm font-semibold rounded-lg disabled:opacity-40 disabled:cursor-not-allowed transition"
              >
                <X className="w-4 h-4" />
                Reject
              </button>
              <button
                onClick={() => decide(active.id, 'Approved')}
                disabled={active.status === 'Approved'}
                className="inline-flex items-center gap-2 px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-semibold rounded-lg disabled:opacity-40 disabled:cursor-not-allowed transition"
              >
                <Check className="w-4 h-4" />
                Approve
              </button>
            </div>
          </div>
        ) : (
          <div className="p-12 text-center"><p className="text-sm text-slate-500">Select an item to review</p></div>
        )}
      </div>
    </div>
  );
}

// ----- New Master Approvals Panel -----
function MasterApprovalsPanel({ submissions, onApprove, onReject, onComment }) {
  const [activeId, setActiveId] = useState(submissions[0]?.id || null);
  const [comment, setComment] = useState('');
  const [rejectReason, setRejectReason] = useState('');
  const [showReject, setShowReject] = useState(false);

  const active = submissions.find((s) => s.id === activeId) || submissions[0];

  if (submissions.length === 0) {
    return (
      <div className="bg-white rounded-xl border border-slate-200 p-12 text-center">
        <Database className="w-10 h-10 mx-auto text-slate-300 mb-3" />
        <p className="text-sm font-semibold text-slate-700">No master submissions awaiting review</p>
        <p className="text-xs text-slate-500 mt-1">Master uploads submitted via the wizard's <span className="font-mono text-violet-700">Submit for Approval</span> action will appear here.</p>
      </div>
    );
  }

  const approve = () => {
    if (!active) return;
    onApprove(active.id, comment.trim() || null);
    toast.success(`${active.moduleLabel} approved`, {
      description: `Version ${active.proposedVersion} is now Active across all forms.`,
    });
    setComment('');
  };

  const reject = () => {
    if (!active || !rejectReason.trim()) {
      toast.error('Please provide a rejection reason');
      return;
    }
    onReject(active.id, rejectReason.trim());
    toast.error(`${active.moduleLabel} rejected`, { description: rejectReason });
    setRejectReason('');
    setShowReject(false);
  };

  const addComment = () => {
    if (!active || !comment.trim()) return;
    onComment(active.id, comment.trim());
    setComment('');
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-[420px,1fr] gap-6">
      {/* Submission list */}
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden flex flex-col">
        <div className="p-4 border-b border-slate-100 flex items-center justify-between">
          <h3 className="text-sm font-bold text-slate-900 flex items-center gap-2">
            <Database className="w-3.5 h-3.5 text-violet-600" />
            Master Queue
          </h3>
          <span className="text-[10px] text-slate-500">{submissions.length} submissions</span>
        </div>
        <div className="divide-y divide-slate-100 overflow-y-auto max-h-[640px]">
          {submissions.map((s) => (
            <button
              key={s.id}
              onClick={() => setActiveId(s.id)}
              data-testid={`master-submission-${s.id}`}
              className={cn(
                'w-full text-left p-4 hover:bg-slate-50/60 transition',
                activeId === s.id && 'bg-violet-50/40 border-l-2 border-l-violet-500'
              )}
            >
              <div className="flex items-start justify-between gap-2 mb-1">
                <span className="font-mono text-[10px] text-slate-500">{s.id}</span>
                <StatusBadge status={s.status} />
              </div>
              <div className="text-sm font-semibold text-slate-900 leading-snug mb-1.5">
                {s.moduleLabel}
              </div>
              <div className="flex items-center gap-2 mb-1.5 flex-wrap">
                <span className="font-mono text-[10px] font-bold px-1.5 py-0.5 rounded bg-slate-100 text-slate-700">{s.currentVersion}</span>
                <span className="text-[10px] text-slate-400">→</span>
                <span className="font-mono text-[10px] font-bold px-1.5 py-0.5 rounded bg-violet-100 text-violet-800">{s.proposedVersion}</span>
              </div>
              <div className="flex items-center justify-between text-[10px] text-slate-500">
                <span>by {s.submittedBy}</span>
                <span>{s.submittedAt}</span>
              </div>
            </button>
          ))}
        </div>
      </div>

      {/* Detail */}
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        {active && (
          <div className="flex flex-col h-full">
            <div className="p-6 border-b border-slate-100">
              <div className="flex items-center gap-2 mb-2 flex-wrap">
                <span className="font-mono text-xs text-slate-500">{active.id}</span>
                <StatusBadge status={active.status} />
                <span className="font-mono text-[10px] font-bold px-1.5 py-0.5 rounded bg-violet-100 text-violet-800">
                  {active.currentVersion} → {active.proposedVersion}
                </span>
              </div>
              <h2 className="text-xl font-bold text-slate-900 leading-tight flex items-center gap-2">
                <Database className="w-5 h-5 text-violet-600" />
                {active.moduleLabel}
              </h2>
              <div className="flex items-center gap-4 mt-2 text-xs text-slate-500 flex-wrap">
                <span>Submitted by <span className="font-medium text-slate-700">{active.submittedBy}</span> ({active.submittedRole})</span>
                <span className="inline-flex items-center gap-1"><Clock className="w-3 h-3" /> {active.submittedAt}</span>
                <span>Reviewer: <span className="font-medium text-slate-700">{active.reviewer}</span></span>
              </div>
            </div>

            <div className="p-6 space-y-5 flex-1 overflow-y-auto">
              <div className="grid grid-cols-3 gap-3">
                <Stat icon={FileSpreadsheet} label="Records" value={active.newRows.length} />
                <Stat icon={Code2} label="Added" value={`+${active.added}`} tone="emerald" />
                <Stat icon={Code2} label="Updated" value={`~${active.updated}`} tone="amber" />
              </div>

              <div className="p-4 rounded-lg bg-amber-50 border border-amber-200 flex items-start gap-2">
                <AlertTriangle className="w-4 h-4 text-amber-700 flex-shrink-0 mt-0.5" />
                <div className="text-xs text-amber-900 leading-relaxed">
                  <span className="font-bold">Approving this submission</span> will publish <span className="font-mono">{active.proposedVersion}</span> to all governance forms (Risk · PCC · Fraud · DLC), archive the previous Active version, and log an audit-trail entry.
                </div>
              </div>

              <div className="rounded-lg border border-slate-200 overflow-hidden">
                <div className="p-3 bg-slate-50 border-b border-slate-100">
                  <div className="text-[10px] font-bold text-slate-500 uppercase tracking-wider">Submitted records preview · first 5</div>
                </div>
                <div className="overflow-x-auto max-h-[200px] overflow-y-auto">
                  <table className="w-full text-xs">
                    <thead className="bg-slate-50/60 sticky top-0">
                      <tr>
                        {Object.keys(active.newRows[0] || {}).slice(0, 5).map((k) => (
                          <th key={k} className="px-3 py-2 text-left text-[9px] font-semibold text-slate-500 uppercase tracking-wider">{k}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {active.newRows.slice(0, 5).map((row, i) => (
                        <tr key={`row-${i}-${row[Object.keys(row)[0]] || ''}`} className="border-t border-slate-100">
                          {Object.keys(row).slice(0, 5).map((k) => (
                            <td key={k} className="px-3 py-2 text-slate-700 truncate max-w-[180px]" title={String(row[k])}>
                              {row[k] || <span className="text-slate-300">—</span>}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Comments */}
              <div>
                <div className="flex items-center gap-2 mb-3">
                  <MessageSquare className="w-4 h-4 text-slate-500" />
                  <h4 className="text-sm font-bold text-slate-900">Reviewer Comments</h4>
                  <span className="text-[10px] text-slate-500">({(active.comments || []).length})</span>
                </div>
                <div className="space-y-2">
                  {(active.comments || []).length === 0 && <div className="text-xs text-slate-400 italic">No comments yet.</div>}
                  {(active.comments || []).map((c, i) => (
                    <div key={`${c.author || 'c'}-${c.at || i}`} className="p-3 rounded-lg bg-slate-50 border border-slate-200">
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-xs font-bold text-slate-900">{c.author}</span>
                        <span className="text-[10px] text-slate-500">{new Date(c.at).toLocaleString()}</span>
                      </div>
                      <p className="text-xs text-slate-700 leading-relaxed">{c.text}</p>
                    </div>
                  ))}
                </div>
                {active.status === 'Pending' && (
                  <div className="mt-3 flex gap-2">
                    <input
                      value={comment}
                      onChange={(e) => setComment(e.target.value)}
                      placeholder="Add a comment for the submitter…"
                      className="flex-1 px-3 py-2 bg-white border border-slate-200 rounded-lg text-xs focus:outline-none focus:border-violet-500 focus:ring-2 focus:ring-violet-500/10"
                      data-testid="master-approval-comment"
                    />
                    <button onClick={addComment} className="px-3 py-2 bg-slate-900 hover:bg-slate-800 text-white text-xs font-semibold rounded-lg inline-flex items-center gap-1.5">
                      <Send className="w-3 h-3" />
                      Post
                    </button>
                  </div>
                )}
              </div>

              {/* Status footer if already decided */}
              {active.status === 'Approved' && (
                <div className="p-4 rounded-lg bg-emerald-50 border border-emerald-200 flex items-start gap-2">
                  <Check className="w-4 h-4 text-emerald-700 flex-shrink-0 mt-0.5" />
                  <div className="text-xs text-emerald-900">
                    Approved by <span className="font-bold">{active.approvedBy}</span> at {active.approvedAt}
                    {active.reviewerComment && <p className="mt-1 italic">"{active.reviewerComment}"</p>}
                  </div>
                </div>
              )}
              {active.status === 'Rejected' && (
                <div className="p-4 rounded-lg bg-red-50 border border-red-200 flex items-start gap-2">
                  <X className="w-4 h-4 text-[#CC0000] flex-shrink-0 mt-0.5" />
                  <div className="text-xs text-[#CC0000]">
                    Rejected by <span className="font-bold">{active.rejectedBy}</span> at {active.rejectedAt}
                    {active.rejectionReason && <p className="mt-1 italic text-slate-700">Reason: "{active.rejectionReason}"</p>}
                  </div>
                </div>
              )}
            </div>

            {active.status === 'Pending' && (
              <div className="px-6 py-4 bg-slate-50 border-t border-slate-100 space-y-3">
                {showReject && (
                  <div className="flex gap-2">
                    <input
                      value={rejectReason}
                      onChange={(e) => setRejectReason(e.target.value)}
                      placeholder="Reason for rejection (required)"
                      className="flex-1 px-3 py-2 bg-white border border-red-200 rounded-lg text-xs focus:outline-none focus:border-[#CC0000] focus:ring-2 focus:ring-[#CC0000]/10"
                      autoFocus
                      data-testid="reject-reason-input"
                    />
                    <button
                      onClick={reject}
                      className="px-3 py-2 bg-[#CC0000] hover:bg-[#B00000] text-white text-xs font-semibold rounded-lg inline-flex items-center gap-1.5"
                      data-testid="confirm-reject-btn"
                    >
                      <X className="w-3 h-3" />
                      Confirm Reject
                    </button>
                    <button
                      onClick={() => { setShowReject(false); setRejectReason(''); }}
                      className="px-3 py-2 bg-white hover:bg-slate-50 border border-slate-200 text-slate-600 text-xs font-semibold rounded-lg"
                    >
                      Cancel
                    </button>
                  </div>
                )}
                {!showReject && (
                  <div className="flex items-center justify-end gap-3">
                    <button
                      onClick={() => setShowReject(true)}
                      className="inline-flex items-center gap-2 px-4 py-2 bg-white hover:bg-red-50 border border-red-200 text-[#CC0000] text-sm font-semibold rounded-lg transition"
                      data-testid="master-reject-btn"
                    >
                      <X className="w-4 h-4" />
                      Reject
                    </button>
                    <button
                      onClick={approve}
                      className="inline-flex items-center gap-2 px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-semibold rounded-lg shadow-sm transition"
                      data-testid="master-approve-btn"
                    >
                      <Check className="w-4 h-4" />
                      Approve & Publish {active.proposedVersion}
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function Stat({ icon: Icon, label, value, tone = 'slate' }) {
  const tones = {
    slate: 'text-slate-900',
    emerald: 'text-emerald-600',
    amber: 'text-amber-600',
  };
  return (
    <div className="p-3 rounded-lg bg-slate-50 border border-slate-200">
      <div className="flex items-center gap-1.5 mb-1">
        <Icon className={cn('w-3 h-3', tones[tone])} />
        <span className="text-[9px] font-bold text-slate-500 uppercase tracking-wider">{label}</span>
      </div>
      <div className={cn('text-lg font-bold', tones[tone])}>{value}</div>
    </div>
  );
}
